import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./revenue.css";
import { getRevenueUser, errorMessage } from "./revenueApi";
import { Avatar, Kpi, Loading, Progress, RangeBar, UserCell } from "./parts";
import { GiftsTab } from "./RevenueSection";
import { compact, dateOnly, fmt, pct } from "./format";

/// One receiver: what they earned (with / without a target), who sent it,
/// their current target and every gift they received in the range.
export default function RevenueUserDetail() {
  const { id } = useParams();
  const [range, setRange] = useState({ from: "", to: "", preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getRevenueUser(id, { from: range.from, to: range.to, limit: 1 })
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError(errorMessage(e, "Could not load user")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, range.from, range.to]);

  const u = data?.user;
  const s = data?.summary;
  const t = data?.target;

  return (
    <div className="rv-wrap">
      <Link to="/dashboard/home" className="rv-back">
        ← Back to revenue
      </Link>

      {error && <div className="rv-error my-3">{error}</div>}
      {loading && !data ? (
        <Loading />
      ) : u ? (
        <>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 my-3">
            <div className="d-flex align-items-center gap-3">
              <Avatar img={u.img} name={u.name} size="lg" />
              <div>
                <h2 className="rv-title">@{u.name}</h2>
                <p className="rv-sub">{u.nick_name}</p>
                <div className="d-flex gap-2 mt-1">
                  <span className="rv-badge rv-badge-gold">{fmt(u.coins)} coins</span>
                  <span className="rv-badge rv-badge-violet">{fmt(u.diamonds)} diamonds</span>
                  <Link to={`/dashboard/wallet?type=user&id=${u.id}`} className="rv-badge rv-badge-green text-decoration-none">
                    Adjust balance →
                  </Link>
                </div>
              </div>
            </div>
            <RangeBar value={range} onChange={setRange} />
          </div>

          <div className="rv-kpis mb-3">
            <Kpi tone="pink" label="Diamonds earned" value={compact(s.diamonds)} title={fmt(s.diamonds)} foot={<>From <b>{fmt(s.gifts_count)}</b> gifts · <b>{fmt(s.senders)}</b> senders</>} />
            <Kpi tone="violet" label="With a target" value={compact(s.diamonds_with_target)} foot={<>{pct(s.diamonds_with_target, s.diamonds)}% of the diamonds</>} />
            <Kpi label="Without a target" value={compact(s.diamonds_without_target)} foot={<>{pct(s.diamonds_without_target, s.diamonds)}% of the diamonds</>} />
            <Kpi tone="gold" label="Gifts value received" value={compact(s.gross)} foot={<>Net after store <b>{fmt(s.net)}</b></>} />
            <Kpi tone="green" label="Generated for category" value={compact(s.category_generated)} foot={<>App got <b>{fmt(s.app_generated)}</b></>} />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="rv-panel h-100">
                <div className="rv-panel-title">Current target</div>
                {t ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        {t.category ? <span className="rv-badge rv-badge-violet">{t.category.name}</span> : <span className="rv-badge rv-badge-grey">No category</span>}
                        <span className={`rv-badge ms-2 ${t.status === "complete" ? "rv-badge-green" : "rv-badge-grey"}`}>{t.status}</span>
                      </div>
                      <span className="text-grey small">
                        {dateOnly(t.start_time)} → {dateOnly(t.end_time)}
                      </span>
                    </div>
                    {[
                      ["Coins", t.achieved_coins, t.coins, "gold"],
                      ["Lives", t.achieved_lives, t.lives, ""],
                    ].map(([label, a, g, tone]) => (
                      <div key={label} className="mb-3">
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-grey">{label}</span>
                          <span>
                            {fmt(a)} / {fmt(g)} ({pct(a, g)}%)
                          </span>
                        </div>
                        <Progress value={pct(a, g)} tone={tone} />
                      </div>
                    ))}
                    <div className="rv-stat-row">
                      <span>Hours</span>
                      <span>
                        {t.achieved_hours} / {t.hours}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="rv-empty">This user has no target.</div>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="rv-panel h-100">
                <div className="rv-panel-title">Top senders</div>
                {data.top_senders?.length ? (
                  data.top_senders.map((r, i) => (
                    <div key={r.user.id} className="rv-stat-row">
                      <span className="d-flex align-items-center gap-2">
                        <span className="text-grey">{i + 1}</span>
                        <UserCell user={r.user} sub={`${fmt(r.gifts_count)} gifts`} />
                      </span>
                      <span>{fmt(r.gross)}</span>
                    </div>
                  ))
                ) : (
                  <div className="rv-empty">No gifts in this range.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rv-panel">
            <div className="rv-panel-title">Gifts received</div>
            <GiftsTab range={range} userId={u.id} />
          </div>
        </>
      ) : null}
    </div>
  );
}
